/**
 * The ONE refusal discriminant for the Azure Storage engine (spec §9). Pure and
 * process-agnostic: every SQL-side registry (query adapter, Table Explorer IPC,
 * metadata provider resolution, notebook / transaction entry points, MCP
 * `query_execute`) refuses `azurestorage` with this constant, mapped into the
 * contract at hand — `code` where the contract has a code slot, the exact
 * `message` where it carries only text.
 */
export const STORAGE_ENGINE_UNSUPPORTED = {
  code: 'UNSUPPORTED_ENGINE',
  message: 'Azure Storage connections have no query language; use the storage browser or the storage_* tools.',
} as const;

/**
 * The ONE read-only refusal for every Azure Storage write (spec §12.2, S2–S5 merge convention): the tooltip of a
 * disabled mutating control in the renderer, and the message of the `authorization` / `ReadOnlyConnection` refusal
 * main's handlers and the MCP storage runner answer. Every slice imports it from here.
 */
export const STORAGE_READ_ONLY_REASON = 'Connection is read-only (edit connection to change)';

/**
 * A service whose default host does not resolve while another service of the account answered (release pass
 * 2026-10-01): usually the account type has no such service (a premium FileStorage account has no Blob, Table or Queue
 * service), but split-horizon or private-endpoint DNS on this computer can answer NXDOMAIN for one service too (review
 * round 2), so the sentence names both. The Test connection leg and the connection's availability say it with this
 * sentence; the DNS answer behind it stays under the leg's Details (`electron/azure-storage/serviceNotOffered.ts`).
 */
export function storageServiceNotOfferedReason(service: string): string {
  return `No ${service} host was found for this account: either the account type has no ${service} service, or this computer's DNS cannot resolve it. If the account has no ${service} service, untick ${service} under Services.`;
}

/**
 * How a Microsoft Entra data-role refusal ends (`electron/azure-storage/data-access/dataRoles.ts`): the account-key way
 * out. Surfaces that state the account-key option on their own, with whether THIS identity may use it — the refusal
 * panel's offer — show the sentence without this tail (release pass 2026-10-01: the panel's Details said "if this
 * identity may read the account keys, switch…" right under the box saying it cannot).
 */
export const DATA_ROLE_KEY_ACCESS_TAIL = " — or, if this identity may read the account keys, switch the connection's data access to the account key.";

/** A data-role refusal's sentence without its account-key tail (it then ends at the role and its scope). */
export function withoutKeyAccessTail(message: string): string {
  return message.replace(DATA_ROLE_KEY_ACCESS_TAIL, '.');
}

/** True when an error is the storage refusal, by `code` or by the exact message (text-only contracts). */
export function isStorageEngineRefusal(error: { code?: string; message?: string } | undefined): boolean {
  if (!error) return false;
  return error.code === STORAGE_ENGINE_UNSUPPORTED.code || error.message === STORAGE_ENGINE_UNSUPPORTED.message;
}
